<!-- ============================================================
  SlipListPanel.vue (<slip-list-panel>) — the ONE slip field.
  Replaces the old "single file input + ดูสลิป link" pair on every screen
  that handles a payment slip. Shows every slip attached to one record
  (max 3 — MULTI_SLIP_DESIGN.md), each with its SlipOK verdict, a view
  button and a delete button, plus a header chip telling whether the slip
  amounts ADD UP to the record's price (ครบ / ขาด / เกิน) and a per-slip
  badge when the transfer date disagrees with the recorded pay date.

  ⚠️ The amount/date checks are OURS, not SlipOK's — SlipOK answers "is this
  slip genuine"; these answer "does it match what the admin typed". The owner
  asked for them because a slip attached to the wrong record used to be
  silently wrong (2026-08-19).

  NOTHING touches the server until the dialog's บันทึก button (owner's call,
  2026-08-21) — so a mis-click is always undoable by cancelling the dialog:
   • picking files only QUEUES them (:pending-files); the PARENT appends them
     to its save request (field `slipImage`)
   • pressing the bin only MARKS the slip (:pending-deletes); the PARENT calls
     deleteQueuedSlips() from utils/slips.js at save time — a marked row stays
     visible, struck through, with an undo button next to it

  Bindings:
   • context + refer            — show the record's saved slips (fetched here)
   • :pending-files   (v-model) — upload queue; required wherever canAdd is true
   • :pending-deletes (v-model) — delete marks; required wherever canDelete is true
  A record that does not exist yet simply passes no context/refer.
============================================================ -->
<template>
  <div class="slip-list">
    <div class="sl-head">
      <span class="sl-title">
        {{ $t('customerCourse.slipList') }}
        <span class="sl-count">({{ totalCount }}/{{ maxSlips }})</span>
      </span>
      <span v-if="amountChip" class="sl-chip" :class="amountChip.cls">
        <span class="mdi" :class="amountChip.icon"></span> {{ amountChip.label }}
      </span>
    </div>

    <div class="sl-rows">
      <!-- โหลดอยู่: กันที่ไว้เงียบ ๆ ไม่ใช้ .id-skel (shimmer แว๊บ ~150ms ตอนเปิด dialog =
           สีเทาวาบที่เจ้าของเห็น) · ข้อความ "กำลังโหลด" ขึ้นเฉพาะตอนช้าเกิน SLOW_MS จริง ๆ
           หมายเหตุ: พาเนลนี้เป็น "ฟิลด์ใน dialog" ไม่ใช่กริด — มาตรฐาน skeleton ของระบบใช้กับ
           กริด และยกเว้นตารางย่อยใน dialog อยู่แล้ว -->
      <div v-if="loading" class="sl-loading">
        <span v-if="loadingSlow">{{ $t('common.loading') }}</span>
      </div>
      <template v-else>
        <!-- สลิปที่บันทึกแล้ว (จากเซิร์ฟเวอร์) -->
        <div v-for="slip in slips" :key="slip.slipid || slip.url" class="sl-row"
          :class="{ 'is-marked': isMarked(slip) }">
          <img v-if="slip.url" :src="slip.url" class="sl-thumb" alt="" @click="openViewer(slip)">
          <span v-else class="mdi mdi-image-off-outline sl-thumb-icon"></span>
          <div class="sl-info">
            <div class="sl-name">
              {{ slip.filename }}
              <span v-if="slipAmount(slip) !== null" class="sl-amt">· {{ $money(slipAmount(slip)) }} ฿</span>
            </div>
            <div class="sl-meta">
              <template v-if="slip.uploaddate">{{ $formatDateTime(slip.uploaddate) }}</template>
              <template v-if="slip.uploadby"> · {{ slip.uploadby }}</template>
              <template v-if="!slip.uploaddate && !slip.uploadby">{{ $t('customerCourse.slipLegacy') }}</template>
            </div>
            <!-- ป้ายอยู่บรรทัดของตัวเอง — เบียดบรรทัดชื่อไฟล์เมื่อไหร่ ชื่อจะแตกทีละตัวอักษร
                 (เจ้าของเจอจริง 21 ส.ค.: badge ยาวบีบคอลัมน์ชื่อเหลือกว้างตัวอักษรเดียว) -->
            <div class="sl-tags">
              <span v-if="isMarked(slip)" class="sl-badge sl-bad">
                <span class="mdi mdi-delete-clock-outline"></span> {{ $t('customerCourse.slipMarkedDelete') }}
              </span>
              <template v-else>
                <span class="sl-badge" :class="statusMeta(slip).cls">
                  <span class="mdi" :class="statusMeta(slip).icon"></span> {{ statusMeta(slip).label }}
                </span>
                <span v-if="slip.paydateMismatch" class="sl-badge sl-warn" v-hint="dateMismatchText(slip)">
                  <span class="mdi mdi-calendar-remove-outline"></span> {{ $t('customerCourse.slipDateMismatch') }}
                </span>
              </template>
            </div>
          </div>
          <div class="sl-acts">
            <button type="button" class="sl-btn" v-hint="$t('customerCourse.viewSlip')"
              :aria-label="$t('customerCourse.viewSlip')" @click="openViewer(slip)">
              <span class="mdi mdi-eye"></span>
            </button>
            <!-- ทำเครื่องหมายไว้แล้ว → ปุ่มกลายเป็น "ยกเลิกการลบ" (กดผิดต้องกู้คืนได้ทันที) -->
            <!-- ⚠️ ต้องเช็ค !disabled ด้วย: จอคอร์สปิดพาเนลจนกว่าจะติ๊ก "จ่ายแล้ว" — ถ้าเช็คแต่ canDelete
                 แอดมินยังกดลบสลิปของใบที่ยังไม่จ่ายได้ แล้วมันหายจริงตอนกดบันทึก -->
            <button v-if="deleteEnabled && isMarked(slip)" type="button" class="sl-btn"
              v-hint="$t('customerCourse.slipUndoDelete')" :aria-label="$t('customerCourse.slipUndoDelete')"
              @click="unmarkDelete(slip)">
              <span class="mdi mdi-undo-variant"></span>
            </button>
            <button v-else-if="deleteEnabled && slip.slipid" type="button" class="sl-btn sl-btn-danger"
              v-hint="$t('customerCourse.slipDelete')" :aria-label="$t('customerCourse.slipDelete')"
              @click="markDelete(slip)">
              <span class="mdi mdi-delete-outline"></span>
            </button>
            <span v-else-if="deleteEnabled" class="sl-btn is-locked" v-hint="$t('customerCourse.slipLegacy')">
              <span class="mdi mdi-lock-outline"></span>
            </span>
          </div>
        </div>

        <!-- ไฟล์ในคิว — ยังไม่ถูกอัพโหลดจนกว่าจะกด "บันทึก" ของ dialog -->
        <div v-for="(file, i) in queued" :key="'q' + i" class="sl-row">
          <span class="mdi mdi-paperclip sl-thumb-icon"></span>
          <div class="sl-info">
            <div class="sl-name">{{ file.name }}</div>
            <div class="sl-meta">{{ fileSize(file.size) }}</div>
            <div class="sl-tags">
              <span class="sl-badge sl-muted"><span class="mdi mdi-tray-arrow-up"></span> {{ $t('customerCourse.slipQueued') }}</span>
            </div>
          </div>
          <div class="sl-acts">
            <button v-if="canAdd" type="button" class="sl-btn" v-hint="$t('btn.cancel')" @click="removePending(i)">
              <span class="mdi mdi-close"></span>
            </button>
          </div>
        </div>

        <div v-if="slips.length === 0 && queued.length === 0" class="sl-empty">{{ $t('customerCourse.slipEmpty') }}</div>
      </template>
    </div>

    <div v-if="canAdd" class="sl-foot">
      <label class="id-upload sl-add" :class="{ 'is-disabled': addDisabled }">
        <span class="mdi mdi-plus"></span> {{ $t('customerCourse.slipAdd') }}
        <input ref="picker" type="file" accept="image/*" multiple hidden
          :disabled="addDisabled" @change="onPick">
      </label>
      <span class="sl-hint">
        <template v-if="markedIds.length > 0">{{ $t('customerCourse.slipDeletePending', { n: markedIds.length }) }}</template>
        <template v-else-if="isFull">{{ $t('customerCourse.slipMax', { n: maxSlips }) }}</template>
        <template v-else-if="queued.length > 0">{{ $t('customerCourse.slipPendingHint') }}</template>
        <template v-else-if="unknownCount > 0">{{ $t('customerCourse.slipUnknownCount', { n: unknownCount }) }}</template>
      </span>
    </div>

    <!-- ดูรูปสลิป + ผลตรวจของใบนั้น -->
    <id-modal v-model="viewerOpen" size="md" icon="mdi-image-search"
      :title="$t('customerCourse.slipTitle')" :subtitle="viewerSubtitle">
      <!-- จอ read-only (ประวัติคอร์ส) ส่ง canReverify=false: ไม่ส่ง context/slipid ให้แผงตรวจ →
           ปุ่ม "ตรวจใหม่" ไม่ขึ้น (เมนู customercoursefinished ไม่มีสิทธิ์ยิง /reverifySlip อยู่แล้ว
           ปุ่มที่กดแล้วได้ 403 ตลอดคือปุ่มหลอก — ผลรีวิว 2026-08-21) -->
      <slip-verification-panel v-if="viewing" :verification="viewing.verify"
        :context="canReverify ? context : null" :refer="canReverify ? refer : null"
        :slipid="canReverify ? viewing.slipid : null" :payment-method="paymentmethod"
        @reverified="onReverified"></slip-verification-panel>
      <div v-if="viewing && viewing.paydateMismatch" class="slip-verify sv-warn" style="margin-top:8px">
        <span class="mdi mdi-calendar-remove-outline"></span>
        <div class="sv-txt"><b>{{ dateMismatchText(viewing) }}</b></div>
      </div>
      <img v-if="viewing && viewing.url" :src="viewing.url"
        style="width:100%;height:auto;border-radius:var(--radius-md);margin-top:10px" alt="">
      <!-- ปุ่มซ้าย ghost / ขวา primary ตามมาตรฐาน IdModal -->
      <template #footer>
        <button type="button" class="id-btn id-btn-ghost" @click="viewerOpen = false">{{ $t('btn.close') }}</button>
        <button v-if="viewing && viewing.slipid" type="button" class="id-btn id-btn-primary"
          :disabled="downloading" @click="downloadSlip">
          <span class="mdi" :class="downloading ? 'mdi-loading mdi-spin' : 'mdi-download'"></span> {{ $t('btn.download') }}
        </button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios';

// ดึงรายการสลิปเสร็จปกติใน ~150ms — ถ้าโชว์ตัวบอกสถานะทันทีจะเห็นเป็นแถบวาบทุกครั้งที่เปิด dialog
// จึงรอถึงเกณฑ์นี้ก่อนค่อยขึ้นข้อความ (เน็ตช้าจริงถึงจะเห็น)
const SLOW_MS = 400;

export default {
  name: 'SlipListPanel',
  props: {
    // มี context+refer = โชว์สลิปที่บันทึกแล้วของรายการนั้น (ดึงเอง) · รายการใหม่ไม่ต้องส่ง
    context: { type: String, default: null },
    refer: { type: [String, Number], default: null },
    // คิวไฟล์รอแนบตอนบันทึก (v-model) — พ่อแม่ append เข้า FormData field 'slipImage' ตอน save
    pendingFiles: { type: Array, default: null },
    // slipid ที่ถูกทำเครื่องหมายว่าจะลบ (v-model) — พ่อแม่เรียก deleteQueuedSlips() ตอน save
    pendingDeletes: { type: Array, default: null },
    // ข้อมูลที่พ่อแม่ดึงมาให้ล่วงหน้าก่อนเปิด dialog (fetchSlipImages ใน utils/slips.js)
    // มีค่า = พาเนลไม่ต้องยิงเองตอน mount ผู้ใช้จึงเห็นรายการครบตั้งแต่วินาทีที่ dialog โผล่
    preloaded: { type: Object, default: null },
    canAdd: { type: Boolean, default: true },
    canDelete: { type: Boolean, default: true },
    // จอ read-only ปิดปุ่ม "ตรวจใหม่" ในตัวดูรูปด้วย (สิทธิ์เมนูของจอนั้นยิง /reverifySlip ไม่ได้)
    canReverify: { type: Boolean, default: true },
    // เงื่อนไขของแต่ละจอ (เช่นคอร์ส: ต้องติ๊ก "จ่ายแล้ว" ก่อน)
    disabled: { type: Boolean, default: false },
    maxSlips: { type: Number, default: 3 },
    // ราคาที่กำลังแก้อยู่ในฟอร์มของ dialog (ถ้าจอนั้นแก้ราคาได้) — ใช้เทียบยอดสลิปแทนราคาที่บันทึกไว้
    priceOverride: { type: [Number, String], default: null },
  },
  emits: ['update:pendingFiles', 'update:pendingDeletes', 'error'],
  data: () => ({
    slips: [],
    price: null,
    paydate: null,
    loading: false,
    // ข้อความ "กำลังโหลด" ขึ้นก็ต่อเมื่อช้าจริง — การดึงปกติเสร็จใน ~150ms ถ้าโชว์ทุกครั้ง
    // จะกลายเป็นแถบวาบตอนเปิด dialog (ปัญหาเดียวกับ .id-skel ที่ถอดออกไป)
    loadingSlow: false,
    paymentmethod: null,
    viewerOpen: false,
    viewing: null,
    downloading: false,
  }),
  computed: {
    serverMode() {
      return !!this.context && this.refer !== null && this.refer !== undefined && this.refer !== '';
    },
    queued() { return this.pendingFiles || []; },
    markedIds() { return this.pendingDeletes || []; },
    // ใบที่จะ "เหลืออยู่จริง" หลังกดบันทึก — ใช้เป็นฐานของทั้งตัวนับและป้ายสรุปยอด เพื่อให้สิ่งที่
    // เห็นบนจอคือผลลัพธ์ที่จะได้ (ทำเครื่องหมายลบ 1 + แนบใหม่ 1 ต้องไม่ถูกตีว่าเกินเพดาน)
    liveSlips() { return this.slips.filter((s) => !this.isMarked(s)); },
    totalCount() { return this.liveSlips.length + this.queued.length; },
    isFull() { return this.totalCount >= this.maxSlips; },
    addDisabled() { return this.disabled || this.isFull; },
    // ลบได้ก็ต่อเมื่อจอนั้นอนุญาต **และ** พาเนลไม่ได้ถูกปิดอยู่
    deleteEnabled() { return this.canDelete && !this.disabled; },
    // ราคาที่ใช้เทียบยอด: ค่าที่กำลังแก้อยู่บนฟอร์มมาก่อนค่าที่เซิร์ฟเวอร์ส่งมา — ไม่งั้นแอดมินแก้ราคา
    // ในฟอร์มแล้วป้ายยังตัดสินจากราคาเดิม ("ครบ" ทั้งที่จะไม่ครบหลังกดบันทึก)
    effectivePrice() {
      const edited = Number(String(this.priceOverride ?? '').toString().replace(/,/g, ''));
      if (this.priceOverride !== null && this.priceOverride !== undefined && this.priceOverride !== ''
          && Number.isFinite(edited)) return edited;
      return this.price;
    },
    // ยอดรวมสลิปที่อ่านยอดได้ คิดจากใบที่จะเหลืออยู่จริง (ไม่ใช่ค่า slipTotal ดิบจากเซิร์ฟเวอร์
    // ซึ่งยังนับใบที่กำลังจะถูกลบอยู่) — กติกาการนับเหมือน backend: verified/amount_mismatch เท่านั้น
    liveTotals() {
      let sum = 0;
      let known = 0;
      this.liveSlips.forEach((s) => {
        const amount = this.slipAmount(s);
        if (amount !== null) { sum += Number(amount); known += 1; }
      });
      return { sum, known, unknown: this.liveSlips.length - known };
    },
    unknownCount() { return this.liveTotals.unknown; },
    viewerSubtitle() {
      if (!this.viewing) return '';
      const parts = [this.viewing.filename];
      if (this.viewing.uploaddate) parts.push(this.$formatDateTime(this.viewing.uploaddate));
      if (this.viewing.uploadby) parts.push(this.viewing.uploadby);
      return parts.join(' · ');
    },
    // ป้ายสรุปหัวรายการ: ยอดรวมสลิปเทียบราคาของใบ (ครบ/ขาด/เกิน) — คนละชั้นกับผลตรวจ SlipOK
    // เทียบระดับสตางค์แบบเดียวกับ sameMoney() ฝั่ง backend (DECIMAL(10,2) มาเป็นสตริง)
    amountChip() {
      const formPrice = this.effectivePrice;
      if (this.liveSlips.length === 0 || formPrice === null || formPrice === undefined) return null;
      const { sum, known } = this.liveTotals;
      if (known === 0) {
        return { cls: 'sl-muted', icon: 'mdi-help-circle-outline', label: this.$t('customerCourse.slipAmountUnknown') };
      }
      const price = Number(formPrice);
      const cents = Math.round(sum * 100) - Math.round(price * 100);
      const paidOf = this.$t('customerCourse.slipAmountPaidOf', { paid: this.$money(sum), price: this.$money(price) });
      const money = this.$money(Math.abs(cents) / 100);
      if (cents === 0) {
        return { cls: 'sl-ok', icon: 'mdi-check-circle-outline', label: this.$t('customerCourse.slipAmountOk') };
      }
      if (cents < 0) {
        return {
          cls: 'sl-warn', icon: 'mdi-alert-outline',
          label: `${this.$t('customerCourse.slipAmountUnder', { n: money })} — ${paidOf}`,
        };
      }
      return {
        cls: 'sl-bad', icon: 'mdi-alert-octagon-outline',
        label: `${this.$t('customerCourse.slipAmountOver', { n: money })} — ${paidOf}`,
      };
    },
  },
  watch: {
    refer() { this.fetchSlips(); },
  },
  mounted() {
    if (this.serverMode && this.preloaded) this.applyData(this.preloaded);
    else this.fetchSlips();
  },
  methods: {
    fileSize(bytes) {
      const mb = Number(bytes || 0) / 1024 / 1024;
      return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(Number(bytes || 0) / 1024)} KB`;
    },
    slipAmount(slip) {
      const v = slip.verify;
      if (!v || v.amount === null || v.amount === undefined) return null;
      return ['verified', 'amount_mismatch'].includes(v.verify_status) ? v.amount : null;
    },
    // ป้ายผลตรวจ SlipOK รายใบ — ชุดไอคอน/สีเดียวกับ SlipVerificationPanel (แหล่งความจริงเดียวกัน)
    statusMeta(slip) {
      const map = {
        verified: { icon: 'mdi-check-decagram', cls: 'sl-ok', label: this.$t('customerCourse.slipVerified') },
        duplicate: { icon: 'mdi-alert-octagon', cls: 'sl-bad', label: this.$t('customerCourse.slipDuplicate') },
        wrong_receiver: { icon: 'mdi-alert-octagon', cls: 'sl-bad', label: this.$t('customerCourse.slipWrongReceiver') },
        amount_mismatch: { icon: 'mdi-alert', cls: 'sl-warn', label: this.$t('customerCourse.slipAmountMismatch') },
        failed: { icon: 'mdi-alert', cls: 'sl-warn', label: this.$t('customerCourse.slipFailed') },
        skipped: { icon: 'mdi-clock-outline', cls: 'sl-muted', label: this.$t('customerCourse.slipSkipped') },
      };
      const status = slip.verify && slip.verify.verify_status;
      return map[status] || { icon: 'mdi-clock-outline', cls: 'sl-muted', label: this.$t('customerCourse.slipVerifyNone') };
    },
    dateMismatchText(slip) {
      const slipDate = slip.verify && slip.verify.trans_datetime ? slip.verify.trans_datetime : null;
      return this.$t('customerCourse.slipDateMismatchDetail', {
        slip: this.$formatDate(slipDate), paydate: this.$formatShortDate(this.paydate),
      });
    },
    // slipTotal/amountStatus/unknownCount ที่เซิร์ฟเวอร์ส่งมาไม่ได้เก็บไว้ — พาเนลคำนวณเองจาก
    // ใบที่จะเหลืออยู่จริง (liveTotals) เพื่อให้ป้ายสรุปสะท้อนเครื่องหมายลบที่ยังไม่ได้บันทึกด้วย
    applyData(data) {
      this.slips = data.slips || [];
      this.price = data.price;
      this.paydate = data.paydate;
      this.paymentmethod = data.paymentmethod;
    },
    async fetchSlips() {
      if (!this.serverMode) {
        this.slips = [];
        return;
      }
      this.loading = true;
      this.loadingSlow = false;
      clearTimeout(this.slowTimer);
      this.slowTimer = setTimeout(() => { this.loadingSlow = true; }, SLOW_MS);
      try {
        const response = await axios.get(
          `${this.baseURL}/slipImages/${this.context}/${encodeURIComponent(this.refer)}`,
          { headers: { Authorization: 'Bearer ' + this.$store.getters.getToken } }
        );
        if (response.data && response.data.success) this.applyData(response.data);
      } catch (error) {
        this.$emit('error', this.$t('customerCourse.slipLoadFail'));
      } finally {
        clearTimeout(this.slowTimer);
        this.loading = false;
        this.loadingSlow = false;
      }
    },
    // เลือกไฟล์ = เข้าคิวเท่านั้น (เจ้าของเคาะ 2026-08-21: ห้ามอัพโหลดจริงจนกว่าจะกด "บันทึก"
    // ของ dialog — ยกเลิก dialog = ทิ้งคิว ไม่มีอะไรถูกเขียนฝั่งเซิร์ฟเวอร์)
    onPick(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';   // เลือกไฟล์เดิมซ้ำได้
      if (files.length === 0) return;
      if (this.pendingFiles === null) {
        // จอที่ลืมผูก :pending-files — บอกโปรแกรมเมอร์ตรง ๆ ดีกว่าเงียบ (ไม่มีทางอัพโหลดได้)
        this.$emit('error', 'SlipListPanel: missing :pending-files binding');
        return;
      }
      if (this.totalCount + files.length > this.maxSlips) {
        this.$emit('error', this.$t('customerCourse.slipMax', { n: this.maxSlips }));
        return;
      }
      this.$emit('update:pendingFiles', [...this.queued, ...files]);
    },
    removePending(index) {
      this.$emit('update:pendingFiles', this.queued.filter((_, i) => i !== index));
    },
    openViewer(slip) {
      this.viewing = slip;
      this.viewerOpen = true;
    },
    // ดาวน์โหลดรูปสลิปที่กำลังดูอยู่ — ต้องผ่าน backend (/slipImage/:slipid/download) ห้ามยิงไป
    // presigned URL ตรง ๆ: bucket ของ Spaces ไม่ได้ตั้ง CORS เบราว์เซอร์จึง fetch ข้ามโดเมนไม่ได้
    // (ยืนยันแล้ว 2026-08-23) และ `<a download>` ข้ามโดเมนจะถูกเมิน attribute → เปิดแท็บใหม่แทน
    // ใบที่ยังไม่ได้บันทึก (คิวรอแนบ) ไม่มี slipid — ปุ่มขึ้นเฉพาะใบที่บันทึกแล้วเท่านั้น
    async downloadSlip() {
      const slip = this.viewing;
      if (!slip || !slip.slipid || this.downloading) return;
      this.downloading = true;
      let href = null;
      try {
        const response = await axios.get(`${this.baseURL}/slipImage/${slip.slipid}/download`, {
          headers: { Authorization: 'Bearer ' + this.$store.getters.getToken },
          responseType: 'blob',
        });
        href = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = href;
        a.download = slip.filename || `slip-${slip.slipid}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (error) {
        this.$emit('error', this.$t('customerCourse.slipDownloadFail'));
      } finally {
        if (href) setTimeout(() => URL.revokeObjectURL(href), 1000);
        this.downloading = false;
      }
    },
    // ปุ่ม "ตรวจใหม่" ใน SlipVerificationPanel ตรวจสำเร็จ — สลับผลของใบนั้นทันที แล้วดึงยอดรวมใหม่
    onReverified(verification) {
      if (this.viewing) this.viewing.verify = verification;
      this.fetchSlips();
    },
    isMarked(slip) {
      return !!slip.slipid && this.markedIds.includes(slip.slipid);
    },
    // กดถังขยะ = ทำเครื่องหมายเท่านั้น ยังไม่ลบจริง (เจ้าของเคาะ 2026-08-21: กดผิดต้องยกเลิกได้)
    // การลบจริงเกิดตอนพ่อแม่กดบันทึก ผ่าน deleteQueuedSlips() ใน utils/slips.js
    markDelete(slip) {
      if (!slip.slipid) return;
      if (this.pendingDeletes === null) {
        // จอที่เปิดปุ่มลบแต่ลืมผูก :pending-deletes — บอกตรง ๆ ดีกว่าเงียบแล้วกดลบไม่ติด
        this.$emit('error', 'SlipListPanel: missing :pending-deletes binding');
        return;
      }
      if (this.isMarked(slip)) return;
      this.$emit('update:pendingDeletes', [...this.markedIds, slip.slipid]);
    },
    unmarkDelete(slip) {
      this.$emit('update:pendingDeletes', this.markedIds.filter((id) => id !== slip.slipid));
    },
  },
  beforeUnmount() {
    clearTimeout(this.slowTimer);
  },
};
</script>
